import React, { useMemo, useState } from "react";
import { CheckCircle2, Download, FileUp, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { useAuth } from "../context/AuthContext";
import { can } from "../lib/permissions";

const STATUS_LABEL = { ready: "Will be created", created: "Created", skipped: "Skipped — reference code exists" };

export default function ProductImport() {
  const { user } = useAuth();
  const allowed = can(user, "supplier.edit");
  const [supplierCode, setSupplierCode] = useState("");
  const [file, setFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  // The validation that unlocked "Import" — any change to the code or file requires validating again.
  const [validatedFor, setValidatedFor] = useState(null);

  const fingerprint = useMemo(
    () => (file ? `${supplierCode.trim().toUpperCase()}|${file.name}|${file.size}|${file.lastModified}` : null),
    [supplierCode, file],
  );
  const canImport = !!fingerprint && validatedFor === fingerprint && result?.dry_run && result.errors.length === 0 && result.product_count > 0;

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get("/admin/product-import/template", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "voocly-product-import-template.csv";
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Could not download the template.");
    }
  };

  const submit = async (dryRun) => {
    if (!supplierCode.trim() || !file) return;
    const body = new FormData();
    body.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/product-import", body, {
        params: { supplier_code: supplierCode.trim(), dry_run: dryRun },
      });
      setResult(data);
      if (dryRun) {
        setValidatedFor(data.errors.length === 0 ? fingerprint : null);
        if (data.errors.length === 0) toast.success(`File is valid: ${data.product_count} product(s) ready to import.`);
        else toast.error(`Found ${data.errors.length} problem(s). Fix them and validate again.`);
      } else if (data.imported) {
        setValidatedFor(null);
        setFile(null);
        setFileInputKey((k) => k + 1);
        toast.success(`Imported ${data.product_count} draft product(s) for ${data.supplier_name || data.supplier_code}.`);
      } else {
        toast.error("Nothing was imported. Review the problems below.");
      }
    } catch (error) {
      setResult(null);
      setValidatedFor(null);
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "The request failed.");
    } finally {
      setBusy(false);
    }
  };

  if (!allowed) {
    return (
      <div data-testid="product-import-page">
        <PageHeader title="Product Import" />
        <p className="pt-6 text-sm text-muted-foreground">You need supplier account or operations permissions to import products.</p>
      </div>
    );
  }

  return (
    <div data-testid="product-import-page">
      <PageHeader
        title="Product Import"
        description="Load a partner's catalogue as draft products. The partner then adds photos, schedules and prices in the partner portal and publishes."
        actions={<Button variant="outline" onClick={downloadTemplate} data-testid="product-import-template"><Download className="mr-2 h-4 w-4" />CSV template</Button>}
      />

      <div className="grid gap-6 pt-6 lg:grid-cols-[minmax(0,420px)_1fr]">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Upload</CardTitle>
            <CardDescription>Validate first. Nothing is saved until you import, and a file with any problem imports nothing.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="import-supplier-code">Supplier code</Label>
              <Input id="import-supplier-code" placeholder="VP-1000" value={supplierCode} onChange={(e) => setSupplierCode(e.target.value)} data-testid="product-import-supplier-code" />
              <p className="text-xs text-muted-foreground">Partners see their code in the account menu of the partner portal.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="import-file">CSV file</Label>
              <Input key={fileInputKey} id="import-file" type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="product-import-file" />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => submit(true)} disabled={busy || !supplierCode.trim() || !file} data-testid="product-import-validate">
                <ShieldAlert className="mr-2 h-4 w-4" />Validate
              </Button>
              <Button onClick={() => submit(false)} disabled={busy || !canImport} data-testid="product-import-submit">
                <FileUp className="mr-2 h-4 w-4" />{canImport ? `Import ${result.product_count} draft${result.product_count === 1 ? "" : "s"}` : "Import drafts"}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">How to fill the template</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="list-disc space-y-1.5 pl-5 text-sm text-muted-foreground">
              <li>One row per <strong>option</strong>. Rows with the same <code>product_key</code> make one product; its product columns are read from its first row.</li>
              <li>Required: <code>product_key</code>, <code>product_type</code>, <code>title</code>, <code>option_name</code>. Every other column may be blank.</li>
              <li>Separate list values with <code>|</code> (e.g. <code>English|Italian</code>). Choice columns must use the wizard's values; the validation lists the allowed ones.</li>
              <li>Give each product a <code>reference_code</code>: re-importing the same file then skips products that already exist.</li>
              <li>Leave out photos, schedules and prices — the partner adds those before publishing.</li>
              <li>In Excel, save as <strong>CSV UTF-8</strong>.</li>
            </ul>
          </CardContent>
        </Card>
      </div>

      {result && (
        <div className="space-y-6 pt-6" data-testid="product-import-result">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <span className="font-medium">{result.supplier_name || "—"} <span className="text-muted-foreground">({result.supplier_code})</span></span>
            <span>{result.product_count} product(s)</span>
            <span>{result.option_count} option(s)</span>
            {result.skipped_count > 0 && <span>{result.skipped_count} skipped</span>}
            {result.imported && <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-4 w-4" />Imported as drafts</span>}
          </div>

          {result.errors.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base text-destructive">{result.errors.length} problem(s) — nothing was saved</CardTitle></CardHeader>
              <CardContent>
                <Table data-testid="product-import-errors">
                  <TableHeader><TableRow><TableHead className="w-20">Row</TableHead><TableHead className="w-40">Product</TableHead><TableHead className="w-48">Column</TableHead><TableHead>Problem</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {result.errors.map((e, i) => (
                      <TableRow key={i}><TableCell>{e.row ?? "—"}</TableCell><TableCell>{e.product_key || "—"}</TableCell><TableCell><code>{e.field}</code></TableCell><TableCell>{e.message}</TableCell></TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {result.products.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Products</CardTitle></CardHeader>
              <CardContent>
                <Table data-testid="product-import-products">
                  <TableHeader><TableRow><TableHead>Product key</TableHead><TableHead>Title</TableHead><TableHead>Reference</TableHead><TableHead className="w-24">Options</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {result.products.map((p) => (
                      <TableRow key={p.product_key}><TableCell>{p.product_key}</TableCell><TableCell>{p.title}</TableCell><TableCell>{p.reference_code || "—"}</TableCell><TableCell>{p.option_count}</TableCell><TableCell>{STATUS_LABEL[p.status] || p.status}</TableCell></TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
